import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { log } from '../utils/DebugLogger'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { featureKeyOf, hasCapability } from './routeMetadata'
import { pluginRoutes } from '../modules'
import { tokenService } from '../services/tokenService'
import { permissionsState, permissionsService } from '../services/permissionsService'
import { useSubscriptionStatus, resolveLockDestination, LOCK_EXEMPT_ROUTE_NAMES } from '../composables/useSubscriptionStatus'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth?: boolean
    requiresGroups?: string[]
    showInMenu?: boolean
    menuOrder?: number
    menuParent?: string | null
    icon?: string
    isParent?: boolean
    permissions?: string[]
    /** Capacidade exigida (skill app-groups) — vale para o menu E para a navegação. */
    capability?: string
    controller?: string
    /**
     * Overrides the tenant-feature key of this route (default: the route
     * name, lowercased). Declare it when two pages of the SAME feature need
     * distinct route names — Site: unidades + leads both gate on `site`.
     */
    feature?: string
    [key: string]: unknown
  }
}

// Import auth pages
import LoginPage from '../domains/auth/LoginPage.vue'
import RegisterPage from '../domains/auth/RegisterPage.vue'
import ConfirmPage from '../domains/auth/ConfirmPage.vue'
import ForgotPasswordPage from '../domains/auth/ForgotPasswordPage.vue'
import ResetPasswordPage from '../domains/auth/ResetPasswordPage.vue'

// Import domain page components
import DashboardPage from '../domains/dashboard/DashboardPage.vue'
import VendasKpiPage from '../domains/vendas-kpi/VendasKpiPage.vue'
import ProducaoKpiPage from '../domains/producao-kpi/ProducaoKpiPage.vue'
import VendasProducaoPage from '../domains/vendas-producao/VendasProducaoPage.vue'
import FranquiasPage from '../domains/franquias/FranquiasPage.vue'
	import FranquiasConfigAvancadaPage from '../domains/franquias/FranquiasConfiguracaoAvancadaPage.vue'
import OrigensPage from '../domains/origens/OrigensPage.vue'
import DespesasPage from '../domains/despesas/DespesasPage.vue'
import FechamentosPage from '../domains/fechamentos/FechamentosPage.vue'
import PlanoContasPage from '../domains/plano-contas/PlanoContasPage.vue'
import DespesasCategoriasPage from '../domains/despesas_categorias/DespesasCategoriasPage.vue'
import DespesasStatusPage from '../domains/despesas_status/DespesasStatusPage.vue'
import ChamadosPage from '../domains/chamados/ChamadosPage.vue'
import ServicosPage from '../domains/servicos/ServicosPage.vue'
import ServicosCategoriasPage from '../domains/servicos_categorias/ServicosCategoriasPage.vue'
import UsersPage from '../domains/users/UsersPage.vue'
import GaleriaPage from '../domains/galeria/GaleriaPage.vue'
import LogsPage from '../domains/logs/LogsPage.vue'
import AgendaPage from '../domains/agenda/AgendaPage.vue'
import ClientePage from '../domains/cliente/GestaoClientesPage.vue'
import ClientesSimplesPage from '../domains/cliente/ClientesSimplesPage.vue'
import CompromissoPage from '../domains/compromisso/CompromissoPage.vue'
import CompromissoServicoPage from '../domains/compromissoservico/CompromissoServicoPage.vue'
import OrcamentoPage from '../domains/orcamento/OrcamentosPage.vue'
import OrcamentoServicoPage from '../domains/orcamentoservico/OrcamentoServicoPage.vue'
import ContasPage from '../domains/contas/ContasPage.vue'
import FormasPagamentoPage from '../domains/formas-pagamento/FormasPagamentoPage.vue'
import FiscalConfigPage from '../domains/subaccounts/fiscal-config/FiscalConfigPage.vue'
import InvoicesPage from '../domains/subaccounts/invoices/InvoicesPage.vue'
import PDFElementsPage from '../domains/pdf_elements/PDFElementsPage.vue'
import OccurrencesPage from '../domains/occurrences/OccurrencesPage.vue'
import ContatoFidelizacaoPage from '../domains/contato_fidelizacao/ContatoFidelizacaoTable.vue'
import DevicesFranquiasPage from '../domains/devices_franquias/DevicesFranquiasPage.vue'
import WhatsappMessageTemplatesPage from '../domains/whatsapp_message_templates/WhatsappMessageTemplatesPage.vue'
import MessagingInboxPage from '../domains/messaging_inbox/MessagingInboxPage.vue'
import MessagingAnalyticsPage from '../domains/messaging_analytics/MessagingAnalyticsPage.vue'
import MensagensProgramadasPage from '../domains/mensagens_programadas/MensagensProgramadasPage.vue'
import MensagensProgramadasLotePage from '../domains/mensagens_programadas_lote/MensagensProgramadasLotePage.vue'
	import AniversariantesPage from '../domains/aniversariantes/AniversariantesPage.vue'
import ProfilePage from '../domains/profile/ProfilePage.vue'
import DebugUserDataPage from '../domains/debug/DebugUserDataPage.vue'

// Import report pages
import VendasOperadoresPage from '../domains/relatorios/VendasOperadoresPage.vue'
import RecebimentoOperadoresPage from '../domains/relatorios/RecebimentoOperadoresPage.vue'
import ComparativoServicosPage from '../domains/relatorios/ComparativoServicosPage.vue'
import VendasProdutosPage from '../domains/relatorios/VendasProdutosPage.vue'
import VendasServicosPage from '../domains/relatorios/VendasServicosPage.vue'
import ComparativoFranquiasPage from '../domains/relatorios/ComparativoFranquiasPage.vue'
import PerformanceFranquiasPage from '../domains/relatorios/PerformanceFranquiasPage.vue'
import ResultadoFinanceiroPage from '../domains/relatorios/ResultadoFinanceiroPage.vue'
import ServicosRealizadosPage from '../domains/relatorios/ServicosRealizadosPage.vue'
import RelatorioClientesPage from '../domains/relatorios/RelatorioClientesPage.vue'
import RelatorioOSPage from '../domains/relatorios-os/RelatorioOSPage.vue'
import SOLaudosAnalysisPage from '../domains/so-orders/SOLaudosAnalysisPage.vue'

// Import payroll pages
import PayrollDashboard from '../domains/payroll/PayrollDashboard.vue'
import PayrollPage from '../domains/payroll/periods/PayrollPage.vue'
import FinancePeriodsPage from '../domains/payroll/FinancePeriodsPage.vue'
import PayrollRefundsPage from '../domains/payroll/refunds/PayrollRefundsPage.vue'
import EmployeeAdvancesPage from '../domains/payroll/advances/EmployeeAdvancesPage.vue'
import EmployeeDeductionsPage from '../domains/payroll/deductions/EmployeeDeductionsPage.vue'
import PaymentRulesPage from '../domains/payroll/payment-rules/PaymentRulesPage.vue'
import EmployeePaymentsPage from '../domains/employee-payments/EmployeePaymentsPage.vue'
import EmployeePaymentDetail from '../domains/employee-payments/EmployeePaymentDetail.vue'

// Import franchises subaccounts pages
import FranchisesSubaccountsPage from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsPage.vue'

// Import payment links page
import PaymentLinksPage from '../domains/payment-links/PaymentLinksPage.vue'

// Import support pages
import TicketsPage from '../domains/support/TicketsPage.vue'

// Import translations admin page
import TranslationsPage from '../domains/translations/TranslationsPage.vue'

// Import languages admin page
import LanguagesPage from '../domains/languages/LanguagesPage.vue'

// Import LGPD registros admin page
import LgpdRegistrosPage from '../domains/lgpd/LgpdRegistrosPage.vue'

// Import Instagram Accounts pages
import InstagramAccountsPage from '../domains/instagram_accounts/InstagramAccountsPage.vue'
import InstagramOAuthCallback from '../domains/instagram_accounts/InstagramOAuthCallback.vue'

// Import SO (Service Order) domain pages
import SOServiceCategoriesPage from '../domains/so-service-categories/SOServiceCategoriesPage.vue'
import SOProductCategoriesPage from '../domains/so-product-categories/SOProductCategoriesPage.vue'
import SOPrioritiesPage from '../domains/so-priorities/SOPrioritiesPage.vue'
import SOItemAttributesPage from '../domains/so-item-attributes/SOItemAttributesPage.vue'
import SOReportAttributesPage from '../domains/so-report-attributes/SOReportAttributesPage.vue'
import SOCatalogFiscalPage from '../domains/so-catalog-fiscal/SOCatalogFiscalPage.vue'
import SOServicesPage from '../domains/so-services/SOServicesPage.vue'
import SOServicePrioritiesPage from '../domains/so-service-priorities/SOServicePrioritiesPage.vue'
import SOAdditionalServicePricesPage from '../domains/so-additional-service-prices/SOAdditionalServicePricesPage.vue'
import SOCombosPage from '../domains/so-combos/SOCombosPage.vue'
import SOCombosRelatorioPage from '../domains/so-combos/SOCombosRelatorioPage.vue'
import SOComboPricesPage from '../domains/so-combo-prices/SOComboPricesPage.vue'
import SOStockPage from '../domains/so-stock/SOStockPage.vue'
import SOOrdersPage from '../domains/so-orders/SOOrdersPage.vue'
import SOAdditionalServicesPage from '../domains/so-additional-services/SOAdditionalServicesPage.vue'
import SOProductionStepsPage from '../domains/so-production-steps/SOProductionStepsPage.vue'
import SOEntryReportPage from '../domains/so-orders/SOEntryReportPage.vue'
import PDVPage from '../domains/pdv/PDVPage.vue'
import PDVLaudosEntradaPage from '../domains/pdv/PDVLaudosEntradaPage.vue'
import PDVLaudosSaidaPage from '../domains/pdv/PDVLaudosSaidaPage.vue'
import PDVScannerPageV2 from '../domains/pdv/PDVScannerPageV2.vue'
import PDVProducaoPage from '../domains/pdv/PDVProducaoPage.vue'
import PDVMovimentacaoPage from '../domains/pdv/PDVMovimentacaoPage.vue'
import PDVExitReportPage from '../domains/pdv/PDVExitReportPage.vue'
import PDVEntregasPage from '../domains/pdv/PDVEntregasPage.vue'
import PDVNovoAtendimentoPage from '../domains/pdv/PDVNovoAtendimentoPage.vue'
import PDVTVPage from '../domains/pdv/PDVTVPage.vue'
import PDVAgendaMensalPage from '../domains/pdv/PDVAgendaMensalPage.vue'
import PDVCheckoutPage from '../domains/pdv/PDVCheckoutPage.vue'
import StaffCreditsPage from '../domains/staff/credits/StaffCreditsPage.vue'
import StaffSubscriptionPage from '../domains/staff/subscription/StaffSubscriptionPage.vue'
import StaffSubscriptionsPage from '../domains/staff/subscription/StaffSubscriptionsPage.vue'
import GracePeriodsPage from '../domains/staff/grace/GracePeriodsPage.vue'
import PaymentGatewaysPage from '../domains/staff/gateways/PaymentGatewaysPage.vue'
import SetupFeeMarketsPage from '../domains/staff/setup-fee/SetupFeeMarketsPage.vue'
import SubscriptionBlockedPage from '../domains/subscription/SubscriptionBlockedPage.vue'
import MyFranchisesPage from '../domains/subscription/MyFranchisesPage.vue'

/**
 * Route Configuration
 *
 * This is the SINGLE SOURCE OF TRUTH for routes and menu structure.
 * The sidebar menu is derived from these routes.
 *
 * Route metadata:
 * - title: Display name
 * - icon: PrimeIcons class (e.g., 'pi-home')
 * - showInMenu: Whether to show in sidebar menu
 * - menuOrder: Order in menu (lower numbers first)
 * - menuParent: Parent menu item name (null for top-level)
 * - permissions: Array of user groups that can access this route
 * - controller: CodeIgniter controller (for reference)
 */

const routes: RouteRecordRaw[] = [
  // Authentication routes (public - no auth required)
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: {
      title: 'Login',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: {
      title: 'Register',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/confirm',
    name: 'Confirm',
    component: ConfirmPage,
    meta: {
      title: 'Confirm Account',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordPage,
    meta: {
      title: 'Forgot Password',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordPage,
    meta: {
      title: 'Reset Password',
      requiresAuth: false,
      showInMenu: false
    }
  },

  {
    path: '/',
    name: 'Dashboard',
    component: DashboardPage,
    meta: {
      title: 'Dashboard',
      icon: 'pi-home',
      showInMenu: true,
      menuOrder: 1,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente', 'Operador', 'Public'],
      controller: 'dashboard',
      requiresAuth: true
    }
  },
  {
    path: '/dashboard',
    name: 'DashboardNew',
    component: DashboardPage,
    meta: {
      title: 'Dashboard',
      icon: 'pi-home',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente', 'Operador', 'Public'],
      controller: 'dashboard',
      requiresAuth: true
    }
  },
  // Dashboard de Vendas e Produção (rota direta, oculta do menu)
  {
    path: '/dashboard-vendas-producao',
    name: 'VendasProducao',
    component: VendasProducaoPage,
    meta: {
      title: 'Dashboard de Vendas e Produção',
      icon: 'pi-chart-line',
      showInMenu: false,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'vendas_kpi,producao_kpi',
      requiresAuth: true
    }
  },

  // Dashboard de Vendas (rota direta, oculta do menu)
  {
    path: '/vendas-kpi',
    name: 'VendasKpi',
    component: VendasKpiPage,
    meta: {
      title: 'Dashboard de Vendas',
      icon: 'pi-chart-line',
      showInMenu: false,
      menuOrder: 2,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'vendas_kpi',
      requiresAuth: true
    }
  },

  // Dashboard de Produção (rota direta, oculta do menu)
  {
    path: '/producao-kpi',
    name: 'ProducaoKpi',
    component: ProducaoKpiPage,
    meta: {
      title: 'Dashboard de Produção',
      icon: 'pi-wrench',
      showInMenu: false,
      menuOrder: 3,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'producao_kpi',
      requiresAuth: true
    }
  },

  // Profile
  {
    path: '/profile',
    name: 'Profile',
    component: ProfilePage,
    meta: {
      title: 'My Profile',
      icon: 'pi-user',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente', 'Operador', 'Public'],
      requiresAuth: true
    }
  },

  // Os endereços antigos (até 2026-10-20 a jornada se chamava "implantação") continuam
  // valendo: levam para os novos, para não quebrar links já enviados.
  { path: '/minha-implantacao', redirect: '/meu-onboarding', meta: { showInMenu: false } },
  { path: '/config/implantacoes', redirect: '/config/onboarding', meta: { showInMenu: false } },
  { path: '/config/implantacoes/checklist', redirect: '/config/onboarding/checklist', meta: { showInMenu: false } },
  { path: '/config/implantacoes/:franquiaId(\\d+)', redirect: (to) => `/config/onboarding/${String(to.params.franquiaId)}`, meta: { showInMenu: false } },

  // Meu onboarding — o franqueado acompanha a jornada da franquia (chamado New Shoes
  // 2026-0079). Fora do menu: chega-se pelo perfil, por Pagamento e uso e pelo 403
  // implantacao_em_andamento, que traz a franquia travada para cá.
  {
    path: '/meu-onboarding',
    name: 'MinhaImplantacao',
    component: () => import('@/domains/implantacao/MinhaImplantacaoPage.vue'),
    meta: {
      title: 'Meu onboarding',
      icon: 'pi-flag',
      showInMenu: false,
      permissions: ['Full', 'Franqueado'],
      requiresAuth: true
    }
  },

  // Agenda
  {
    path: '/agenda',
    name: 'Agenda',
    component: AgendaPage,
    meta: {
      title: 'Agenda',
      icon: 'pi-calendar',
      showInMenu: true,
      menuOrder: 10,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente', 'Operador'],
      controller: 'agenda'
    }
  },

  // Aniversariantes
  {
    path: '/aniversariantes',
    name: 'Aniversariantes',
    component: AniversariantesPage,
    meta: {
      title: 'Aniversariantes',
      icon: 'pi-calendar-times',
      showInMenu: true,
      menuOrder: 110,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'aniversariantes'
    }
  },

  // Gestão de Clientes
  {
    path: '/clientes',
    name: 'Clientes',
    component: ClientePage,
    meta: {
      title: 'Gestão de Clientes',
      icon: 'pi-users',
      showInMenu: true,
      menuOrder: 20,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'clientes'
    }
  },

  // Clientes (lista simplificada — read-only)
  {
    path: '/clientes-simples',
    name: 'ClientesSimples',
    component: ClientesSimplesPage,
    meta: {
      title: 'Clientes',
      icon: 'pi-users',
      showInMenu: true,
      menuOrder: 21,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'clientes',
      requiresAuth: true
    }
  },

  // Compromissos
  {
    path: '/compromissos',
    name: 'Compromissos',
    component: CompromissoPage,
    meta: {
      title: 'Compromissos',
      icon: 'pi-calendar-plus',
      showInMenu: true,
      menuOrder: 30,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente', 'Operador'],
      controller: 'compromissos'
    }
  },

  // Orçamentos
  {
    path: '/orcamentos',
    name: 'Orcamentos',
    component: OrcamentoPage,
    meta: {
      title: 'Orçamentos',
      icon: 'pi-file-edit',
      showInMenu: true,
      menuOrder: 40,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'orcamentos'
    }
  },

  // Retired user credits page — folded into /pagamento-e-uso.
  {
    path: '/creditos-ia',
    redirect: '/pagamento-e-uso',
  },

  // Assistente — the /assistente/config route was removed in M7b. The four
  // config/audit panels moved into StaffCreditsPage (/config/creditos-ia, F2/OD-012).
  // The chat UI is now a sidebar drawer (AssistantSidebar, F2).

  // Credits console (Full-only; cost/margin/COGS appear ONLY here) — top-level menu item.
  // Admin AI-credits console — Full-only, grouped under Configurações next to Assinaturas.
  {
    path: '/config/creditos-ia',
    name: 'StaffCredits',
    component: StaffCreditsPage,
    meta: {
      title: 'Configurações IA',
      icon: 'pi-bolt',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 7
    }
  },

  // Subscription admin console (Full/Master-gated).
  {
    path: '/config/assinaturas-planos',
    name: 'StaffSubscription',
    component: StaffSubscriptionPage,
    meta: {
      title: 'Planos de assinaturas',
      icon: 'pi-credit-card',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 2
    }
  },

  // Subscription admin roster (Full/Master-gated).
  {
    path: '/config/assinaturas',
    name: 'StaffSubscriptions',
    component: StaffSubscriptionsPage,
    meta: {
      title: 'Assinaturas',
      icon: 'pi-id-card',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 3
    }
  },

  // Grace periods admin console (Full/Master-gated).
  {
    path: '/config/periodo-graca',
    name: 'GracePeriods',
    component: GracePeriodsPage,
    meta: {
      title: 'Períodos de Carência',
      icon: 'pi-clock',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 4,
    },
  },

  // Onboarding (até 2026-10-20 "Implantações") — a jornada do franqueado da venda à operação liberada
  // (chamado New Shoes 2026-0079, F1). Full.
  {
    path: '/config/onboarding',
    name: 'Implantacoes',
    component: () => import('@/domains/implantacao/ImplantacoesPage.vue'),
    meta: {
      title: 'Onboarding',
      icon: 'pi-flag',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 0
    }
  },
  {
    path: '/config/onboarding/checklist',
    name: 'ImplantacaoChecklist',
    component: () => import('@/domains/implantacao/ChecklistDaImplantacaoPage.vue'),
    meta: {
      title: 'Checklist do onboarding',
      icon: 'pi-list-check',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: false,
      permissions: ['Full']
    }
  },
  {
    path: '/config/onboarding/:franquiaId(\\d+)',
    name: 'ImplantacaoDetalhe',
    component: () => import('@/domains/implantacao/ImplantacaoDetalhePage.vue'),
    meta: {
      title: 'Onboarding',
      icon: 'pi-flag',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: false,
      permissions: ['Full']
    }
  },

  // Setup-fee markets (Full-only).
  {
    path: '/config/taxas-implantacao',
    name: 'SetupFeeMarkets',
    component: SetupFeeMarketsPage,
    meta: {
      title: 'Taxas de implantação',
      icon: 'pi-wallet',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 1
    }
  },

  // Contrato da franquia: o sistema monta o contrato padrão e o DocuSign colhe as assinaturas
  // (plano perfil/documentos/contrato §3a). Full.
  {
    path: '/config/contratos',
    name: 'Contratos',
    component: () => import('@/domains/contratos/ContratosPage.vue'),
    meta: {
      title: 'Contratos',
      icon: 'pi-file-edit',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 1
    }
  },
  // Modelo padrão do contrato: as cláusulas, uma versão nova a cada gravação. Chega-se pela
  // tela de Contratos. Full.
  {
    path: '/config/contratos/modelo',
    name: 'ContratoModelo',
    component: () => import('@/domains/contratos/ContratoModeloPage.vue'),
    meta: {
      title: 'Modelo padrão do contrato',
      icon: 'pi-file-edit',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: false,
      permissions: ['Full']
    }
  },

  // Payment Gateways console (Full-only).
  {
    path: '/config/payment-gateways',
    name: 'PaymentGateways',
    component: PaymentGatewaysPage,
    meta: {
      title: 'Gateways de Pagamento',
      icon: 'pi-server',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 8,
    },
  },

  // Subscription blocked — auth-only, NO requiresGroups (any gated logged-in user must reach it).
  // M5b: the httpService interceptor (not this guard) is the recovery boundary; this route
  // is merely the destination the interceptor redirects to when PHP returns subscription_blocked.
  {
    path: '/subscription/blocked',
    name: 'SubscriptionBlocked',
    component: SubscriptionBlockedPage,
    meta: {
      title: 'Acesso bloqueado',
      requiresAuth: true,
      showInMenu: false
    }
  },

  {
    path: '/minhas-assinaturas',
    name: 'MinhasAssinaturas',
    redirect: '/pagamento-e-uso',
  },
  {
    path: '/pagamento-e-uso',
    name: 'PagamentoEUso',
    component: MyFranchisesPage,
    meta: {
      title: 'Pagamento e uso',
      icon: 'pi-receipt',
      showInMenu: true,
      menuOrder: 99,
      menuParent: null,
      requiresAuth: true,
      requiresGroups: ['Full', 'Franqueado', 'Gerente'],
      permissions: ['Full', 'Franqueado', 'Gerente'],
    }
  },

  // Despesas
  {
    path: '/despesas',
    name: 'Despesas',
    component: DespesasPage,
    meta: {
      title: 'Despesas',
      icon: 'pi-chart-line',
      showInMenu: true,
      menuOrder: 60,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'despesas'
    }
  },

  // Fechamentos Mensais (Plano de Contas)
  {
    path: '/fechamentos',
    name: 'Fechamentos',
    component: FechamentosPage,
    meta: {
      title: 'Fechamentos',
      icon: 'pi-calendar',
      showInMenu: true,
      menuOrder: 65,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'fechamentos'
    }
  },

  // Plano de Contas Template Management
  {
    path: '/plano-contas',
    name: 'PlanoContas',
    component: PlanoContasPage,
    meta: {
      title: 'Plano de Contas',
      icon: 'pi-list',
      showInMenu: true,
      menuOrder: 67,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'planoscontas'
    }
  },

  // Financeiro > Metas Financeiras
  {
    path: '/minhas-metas',
    name: 'MinhasMetas',
    component: () => import('../domains/franquias/MinhasMetasPage.vue'),
    meta: {
      title: 'Metas Financeiras',
      icon: 'pi-chart-line',
      showInMenu: true,
      menuOrder: 66,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
    }
  },

  // Galeria
  {
    path: '/galeria',
    name: 'Galeria',
    component: GaleriaPage,
    meta: {
      title: 'Galeria',
      icon: 'pi-images',
      showInMenu: true,
      menuOrder: 70,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'galeria'
    }
  },



  // Ações de Marketing — per-franchise marketing actions with photos.
  // Feature key = lowercased route name: 'acoesmarketing' (config/features.php).
  {
    path: '/marketing/acoes',
    name: 'AcoesMarketing',
    component: () => import('@/domains/marketing/AcoesMarketingPage.vue'),
    meta: {
      title: 'Ações de Marketing',
      icon: 'pi-megaphone',
      showInMenu: true,
      menuOrder: 71,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'marketing/acoes'
    }
  },

  // WhatsApp Franquias
    {
      path: '/whatsapp',
      name: 'Mensagens',
      redirect: '/mensagens-programadas',
      meta: {
        title: 'Mensagens',
        icon: 'pi-whatsapp',
        showInMenu: true,
        menuOrder: 70,
        menuParent: null,
        permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
        controller: 'galeria'
      }
    },

  // Mensagens Programadas
  {
    path: '/mensagens-programadas',
    name: 'MensagensProgramadas',
    component: MensagensProgramadasPage,
    meta: {
      title: 'Mensagens Programadas',
      icon: 'pi-send',
      showInMenu: true,
      menuOrder: 90,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'mensagens_programadas'
    }
  },

  // Mensagens Programadas Lote
  {
    path: '/mensagens-programadas-lote',
    name: 'MensagensProgramadasLote',
    component: MensagensProgramadasLotePage,
    meta: {
      title: 'Lote de Mensagens Programadas',
      icon: 'pi-inbox',
      showInMenu: true,
      menuOrder: 100,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'mensagens_programadas_lote'
    }
  },

  // Messaging Inbox
  {
    path: '/messaging-inbox',
    name: 'MessagingInbox',
    component: MessagingInboxPage,
    meta: {
      title: 'Inbox',
      icon: 'pi-comments',
      showInMenu: true,
      menuOrder: 85,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'config/devices_franquias',
      fullScreen: true
    }
  },

  // WhatsApp Analytics
  {
    path: '/messaging-analytics',
    name: 'MessagingAnalytics',
    component: MessagingAnalyticsPage,
    meta: {
      title: 'Dashboard',
      icon: 'pi-chart-bar',
      showInMenu: true,
      menuOrder: 86,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/whatsapp_analytics'
    }
  },

  // Payroll (Parent)
  {
    path: '/finance',
    name: 'Financeiro',
    redirect: '/payroll/dashboard',
    meta: {
      title: 'Financeiro',
      icon: 'pi-money-bill',
      showInMenu: true,
      menuOrder: 110,
      menuParent: null,
      permissions: ['Full', 'Franqueado'],
      isParent: true
    }
  },

  // Controle de Caixa (parent menu)
  {
    path: '/controle-caixa',
    name: 'ControleCaixa',
    redirect: '/caixa/lista',
    meta: {
      title: 'Controle de Caixa',
      icon: 'pi-wallet',
      showInMenu: true,
      menuOrder: 111,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      isParent: true
    }
  },

        // Recebíveis - 360 view of all recebimentos
        {
          path: '/finance/recebiveis',
          name: 'Recebiveis',
          component: () => import('@/domains/recebimento/RecebiveisPage.vue'),
          meta: {
            requiresAuth: true,
            title: 'Recebíveis',
            icon: 'pi-dollar',
            showInMenu: true,
            menuOrder: 51,
            menuParent: 'Financeiro',
            permissions: ['Full', 'Franqueado', 'Gerente']
          }
        },

    // Payment Links
    {
      path: '/finance/payment-links',
      name: 'PaymentLinks',
      component: PaymentLinksPage,
      meta: {
        title: 'Links de Pagamento',
        icon: 'pi-link',
        showInMenu: true,
        menuOrder: 52,
        menuParent: 'Financeiro',
        permissions: ['Full', 'Franqueado', 'Gerente'],
        controller: 'subaccounts/links',
        requiresAuth: true
      }
    },

    // Anticipations
    {
      path: '/finance/anticipations',
      name: 'Anticipations',
      component: () => import('@/domains/anticipations/AnticipationsPage.vue'),
      meta: {
        title: 'Antecipações',
        icon: 'pi-clock',
        showInMenu: true,
        menuOrder: 53,
        menuParent: 'Financeiro',
        permissions: ['Full', 'Franqueado'],
        controller: 'subaccounts/anticipations',
        requiresAuth: true
      }
    },

    // Faturas — emitted-invoice listing (sibling to Recebíveis / Payment Links)
    {
      path: '/finance/faturas',
      name: 'Faturas',
      component: InvoicesPage,
      meta: {
        title: 'Faturas',
        icon: 'pi-receipt',
        showInMenu: true,
        menuOrder: 54,
        menuParent: 'Financeiro',
        permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
        controller: 'subaccounts/invoices',
        requiresAuth: true
      }
    },

      // Compromissos Pagos e Não Pagos
      {
        path: '/finance/compromissos-pagos-nao-pagos',
        name: 'CompromissosPagosNaoPagos',
        component: () => import('@/domains/recebimento/CompromissosPagosNaoPagosPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Compromissos pagos e não pagos',
          icon: 'pi-chart-line',
          showInMenu: true,
          menuOrder: 60,
          menuParent: 'Financeiro',
          permissions: ['Full', 'Franqueado', 'Gerente']
        }
      },
    
      // Recebimentos por Forma de Pagamento e Operador
      {
        path: '/finance/recebimentos-por-forma-pagamento-operador',
        name: 'RecebimentosPorFormaPagamentoOperador',
        component: () => import('@/domains/recebimento/RecebimentosPorFormaPagamentoOperadorPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Recebimentos por Forma de Pagamento e Operador',
          icon: 'pi-wallet',
          showInMenu: true,
          menuOrder: 61,
          menuParent: 'Financeiro',
          permissions: ['Full', 'Franqueado', 'Gerente']
        }
      },



  // // Payroll Dashboard
  // {
  //   path: '/payroll/dashboard',
  //   name: 'PayrollDashboard',
  //   component: PayrollDashboard,
  //   meta: {
  //     title: 'Payroll Dashboard',
  //     icon: 'pi-chart-line',
  //     showInMenu: true,
  //     menuOrder: 111,
  //     menuParent: 'Payroll',
  //     permissions: ['Full', 'Franqueado', 'Gerente'],
  //     controller: 'payroll',
  //     requiresAuth: true
  //   }
  // },

  // Finance Periods Management (Tabbed view with Payroll, Refunds, Advances, Payment Rules)
  {
    path: '/finance/periods',
    name: 'FinancePeriodsPage',
    component: FinancePeriodsPage,
    meta: {
      title: 'Gestão de Pessoal',
      icon: 'pi-calendar',
      showInMenu: true,
      menuOrder: 112,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'payroll',
      requiresAuth: true
    }
  },

  // Payroll Refunds (Expense Reimbursements) - Hidden from menu, accessible via tabs or direct URL
  {
    path: '/finance/refunds',
    name: 'PayrollRefundsPage',
    component: PayrollRefundsPage,
    meta: {
      title: 'Reembolsos de Despesas',
      icon: 'pi-wallet',
      showInMenu: false,
      menuOrder: 113,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'payroll',
      requiresAuth: true
    }
  },

  // Payroll Advances (Salary Advance Requests) - Hidden from menu, accessible via tabs or direct URL
  {
    path: '/finance/advances',
    name: 'EmployeeAdvancesPage',
    component: EmployeeAdvancesPage,
    meta: {
      title: 'Adiantamentos Salariais',
      icon: 'pi-money-bill',
      showInMenu: false,
      menuOrder: 114,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'payroll',
      requiresAuth: true
    }
  },

  // Employee Deductions - Hidden from menu, accessible via tabs or direct URL
  {
    path: '/finance/employee-deductions',
    name: 'EmployeeDeductionsPage',
    component: EmployeeDeductionsPage,
    meta: {
      title: 'Deduções de Colaboradores',
      icon: 'pi-minus-circle',
      showInMenu: false,
      menuOrder: 114,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'payroll',
      requiresAuth: true
    }
  },

  // Payment Rules - Hidden from menu, accessible via tabs or direct URL
  {
    path: '/finance/payment-rules',
    name: 'PaymentRulesPage',
    component: PaymentRulesPage,
    meta: {
      title: 'Regras de Pagamento',
      icon: 'pi-cog',
      showInMenu: false,
      menuOrder: 115,
      menuParent: 'Financeiro',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'payroll',
      requiresAuth: true
    }
  },

  // // My Payments (Operador View)
  // {
  //   path: '/my-payments',
  //   name: 'EmployeePaymentsPage',
  //   component: EmployeePaymentsPage,
  //   meta: {
  //     title: 'My Payments',
  //     icon: 'pi-wallet',
  //     showInMenu: true,
  //     menuOrder: 114,
  //     menuParent: 'Payroll',
  //     permissions: ['Operador'],
  //     controller: 'payroll',
  //     requiresAuth: true
  //   }
  // },

  // {
  //   path: '/my-payments/:id',
  //   name: 'EmployeePaymentDetail',
  //   component: EmployeePaymentDetail,
  //   meta: {
  //     title: 'Payment Detail',
  //     showInMenu: false,
  //     permissions: ['Operador'],
  //     controller: 'payroll',
  //     requiresAuth: true
  //   }
  // },

  // Relatórios (Parent)
  {
    path: '/relatorios',
    name: 'Relatorios',
    redirect: '/relatorios/vendas-operadores',
    meta: {
      title: 'Relatórios',
      icon: 'pi-chart-bar',
      showInMenu: true,
      menuOrder: 200,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      isParent: true
    }
  },

  // Relatórios de OS (chamado NS 2026-0084) — a marca que trabalha com OS.
  // Feature `relatoriosos`; desligar `soorders` desliga junto (backend).
  {
    path: '/relatorios-os',
    name: 'RelatoriosOS',
    redirect: '/relatorios-os/vendas-por-cliente',
    meta: {
      title: 'Relatórios',
      icon: 'pi-chart-bar',
      showInMenu: true,
      menuOrder: 200,
      menuParent: null,
      // Atendente: só o Consumo por cliente (NS 2026-0104), os outros filhos não o aceitam.
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      feature: 'relatoriosos',
      isParent: true
    }
  },
  {
    path: '/relatorios-os/vendas-por-cliente',
    name: 'VendasPorCliente',
    component: RelatorioOSPage,
    props: { tipo: 'clientes' },
    meta: {
      title: 'Vendas por cliente',
      icon: 'pi-users',
      showInMenu: true,
      menuOrder: 201,
      menuParent: 'RelatoriosOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      feature: 'relatoriosos',
      controller: 'relatorios/relatorios_os'
    }
  },
  {
    path: '/relatorios-os/vendas-por-servico',
    name: 'VendasPorServico',
    component: RelatorioOSPage,
    props: { tipo: 'servicos' },
    meta: {
      title: 'Vendas por serviço',
      icon: 'pi-briefcase',
      showInMenu: true,
      menuOrder: 202,
      menuParent: 'RelatoriosOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      feature: 'relatoriosos',
      controller: 'relatorios/relatorios_os'
    }
  },
  {
    path: '/relatorios-os/produtividade-por-usuario',
    name: 'ProdutividadePorUsuario',
    component: RelatorioOSPage,
    props: { tipo: 'produtividade' },
    meta: {
      title: 'Produtividade por usuário',
      icon: 'pi-chart-line',
      showInMenu: true,
      menuOrder: 203,
      menuParent: 'RelatoriosOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      feature: 'relatoriosos',
      controller: 'relatorios/relatorios_os'
    }
  },
  // Consumo por cliente (NS 2026-0104): os grupos do relatório de clientes.
  {
    path: '/relatorios-os/consumo-por-cliente',
    name: 'ConsumoPorCliente',
    component: RelatorioOSPage,
    props: { tipo: 'consumo' },
    meta: {
      title: 'Consumo por cliente',
      icon: 'pi-replay',
      showInMenu: true,
      menuOrder: 204,
      menuParent: 'RelatoriosOS',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      feature: 'relatoriosos',
      controller: 'relatorios/relatorios_os'
    }
  },
  // Combos pré-pagos por unidade (NS 2026-0090). Feature `combos`.
  {
    path: '/relatorios-os/combos',
    name: 'SOCombosRelatorio',
    component: SOCombosRelatorioPage,
    meta: {
      title: 'Combos por unidade',
      icon: 'pi-box',
      showInMenu: true,
      menuOrder: 204,
      menuParent: 'RelatoriosOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      feature: 'combos',
      controller: 'so/combo_relatorios'
    }
  },

  // Relatórios - Vendas por Operadores
  {
    path: '/relatorios/vendas-operadores',
    name: 'VendasOperadores',
    component: VendasOperadoresPage,
    meta: {
      title: 'Vendas por Operadores',
      icon: 'pi-dollar',
      showInMenu: true,
      menuOrder: 201,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'relatorios/vendas_operadores'
    }
  },



  // Relatórios - Recebimento por Operadores
  {
    path: '/relatorios/recebimento-operadores',
    name: 'RecebimentoOperadores',
    component: RecebimentoOperadoresPage,
    meta: {
      title: 'Recebimento por Operadores',
      icon: 'pi-wallet',
      showInMenu: true,
      menuOrder: 202,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'relatorios/recebimento_operadores'
    }
  },

  // Relatórios - Comparativo de Serviços
  {
    path: '/relatorios/comparativo-servicos',
    name: 'ComparativoServicos',
    component: ComparativoServicosPage,
    meta: {
      title: 'Comparativo de Serviços',
      icon: 'pi-chart-line',
      showInMenu: true,
      menuOrder: 203,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'relatorios/comparativo_servicos'
    }
  },

  // Relatórios - Venda de produtos (chamado 2026-0046)
  {
    path: '/relatorios/vendas-produtos',
    name: 'VendasProdutos',
    component: VendasProdutosPage,
    meta: {
      title: 'Venda de produtos',
      icon: 'pi-shopping-bag',
      showInMenu: true,
      menuOrder: 204,
      menuParent: 'Relatorios',
      // Full-only: o agregado da rede é da franqueadora por enquanto
      // (decisão do usuário, 2026-09-11). O backend aplica o mesmo gate.
      permissions: ['Full'],
      controller: 'relatorios/vendas_produtos'
    }
  },

  // Relatórios - Venda por serviço e prioridade (chamado 2026-0086)
  {
    path: '/relatorios/vendas-servicos',
    name: 'VendasServicos',
    component: VendasServicosPage,
    meta: {
      title: 'Venda por serviço e prioridade',
      icon: 'pi-th-large',
      showInMenu: true,
      menuOrder: 205,
      menuParent: 'Relatorios',
      // Cada grupo vê só as franquias do seu filtro; o backend aplica o mesmo gate.
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'relatorios/vendas_servicos'
    }
  },

  // Relatórios - Comparativo de Franquias
  {
    path: '/relatorios/comparativo-franquias',
    name: 'ComparativoFranquias',
    component: ComparativoFranquiasPage,
    meta: {
      title: 'Comparativo de Franquias',
      icon: 'pi-building',
      showInMenu: true,
      menuOrder: 204,
      menuParent: 'Relatorios',
      permissions: ['Full'],
      controller: 'relatorios/comparativo_franquias'
    }
  },

  // Relatórios - Performance de Franquias
  {
    path: '/relatorios/performance-franquias',
    name: 'PerformanceFranquias',
    component: PerformanceFranquiasPage,
    meta: {
      title: 'Performance de Franquias',
      icon: 'pi-chart-pie',
      showInMenu: true,
      menuOrder: 205,
      menuParent: 'Relatorios',
      permissions: ['Full'],
      controller: 'relatorios/performance_franquias'
    }
  },

  // Relatórios - Resultado Financeiro
  {
    path: '/relatorios/resultado-financeiro',
    name: 'ResultadoFinanceiro',
    component: ResultadoFinanceiroPage,
    meta: {
      title: 'Resultado Financeiro',
      icon: 'pi-calculator',
      showInMenu: true,
      menuOrder: 206,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'relatorios/resultado_financeiro'
    }
  },

  // Relatórios - Serviços Realizados
  {
    path: '/relatorios/servicos-realizados',
    name: 'ServicosRealizados',
    component: ServicosRealizadosPage,
    meta: {
      title: 'Serviços Realizados',
      icon: 'pi-check-square',
      showInMenu: true,
      menuOrder: 207,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'relatorios/servicos_realizados'
    }
  },

  // Relatórios - Relatório de Clientes
  {
    path: '/relatorios/relatorio-clientes',
    name: 'RelatorioClientes',
    component: RelatorioClientesPage,
    meta: {
      title: 'Relatório de Clientes',
      icon: 'pi-users',
      showInMenu: true,
      menuOrder: 210,
      menuParent: 'Relatorios',
      permissions: ['Full', 'Gerente', 'Franqueado', 'Atendente'],
      controller: 'relatorios/relatorio_clientes'
    }
  },

  // PDV - Análise de Laudos
  {
    path: '/pdv/analise-laudos',
    name: 'AnaliseLaudos',
    component: SOLaudosAnalysisPage,
    meta: {
      title: 'Análise de Laudos',
      icon: 'pi pi-file-check',
      showInMenu: true,
      menuOrder: 49,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/orders'
    }
  },

  // Configurações (Parent)
  {
    path: '/config',
    name: 'Configuracoes',
    redirect: '/franquias',
    meta: {
      title: 'Configurações',
      icon: 'pi-cog',
      showInMenu: true,
      menuOrder: 300,
      menuParent: null,
      permissions: ['Full'],
      // Franqueado e Gerente não veem Configurações no menu por enquanto (Ivan, 2026-09-23) — nem pelo
      // caminho de uma rota por capacidade (Usuários). Os filhos somem junto.
      hideFor: ['Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // =========================================================================
  // CONFIG SUB-GROUPS
  // =========================================================================

  {
    path: '/config-setup-assinatura-ia',
    name: 'ConfigSetupAssinaturaIa',
    redirect: '/config/taxas-implantacao',
    meta: {
      title: 'Setup, Assinatura e IA',
      icon: 'pi-credit-card',
      showInMenu: true,
      menuOrder: 5,
      menuParent: 'Configuracoes',
      permissions: ['Full'],
      isParent: true,
    },
  },

  // Config > Financeiro (Parent)
  {
    path: '/config-financeiro',
    name: 'ConfigFinanceiro',
    redirect: '/contas',
    meta: {
      title: 'Financeiro',
      icon: 'pi-wallet',
      showInMenu: true,
      menuOrder: 10,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // Config > Financeiro > Contas
  {
    path: '/contas',
    name: 'Contas',
    component: ContasPage,
    meta: {
      title: 'Contas',
      icon: 'pi-money-bill',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigFinanceiro',
      permissions: ['Full'],
      controller: 'config/contas'
    }
  },

  // Config > Financeiro > Subcontas de Franquias
  {
    path: '/subaccounts/franchises',
    name: 'FranchisesSubaccounts',
    component: FranchisesSubaccountsPage,
    meta: {
      title: 'Subcontas de Franquias',
      icon: 'pi-wallet',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigFinanceiro',
      permissions: ['Full', 'Franqueado'],
      controller: 'config/franchises_subaccounts'
    }
  },

  // Config > Financeiro > Configuração Fiscal (per-franchise)
  {
    path: '/subaccounts/fiscal-config',
    name: 'FiscalConfig',
    component: FiscalConfigPage,
    meta: {
      title: 'Configuração Fiscal',
      icon: 'pi-file',
      showInMenu: true,
      menuOrder: 2.5,
      menuParent: 'ConfigFinanceiro',
      permissions: ['Full', 'Franqueado'],
      controller: 'subaccounts/invoice_config'
    }
  },


  // Config > Financeiro > Formas de Pagamento
  {
    path: '/formas-pagamento',
    name: 'FormasPagamento',
    component: FormasPagamentoPage,
    meta: {
      title: 'Formas de Pagamento',
      icon: 'pi-credit-card',
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'ConfigFinanceiro',
      permissions: ['Full', 'Franqueado'],
      controller: 'config/formas_pagamento'
    }
  },

  // Config > Despesas (Parent)
  {
    path: '/config-despesas',
    name: 'ConfigDespesas',
    redirect: '/despesas-categorias',
    meta: {
      title: 'Despesas',
      icon: 'pi-money-bill',
      showInMenu: true,
      menuOrder: 20,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // Config > Despesas > Categorias
  {
    path: '/despesas-categorias',
    name: 'DespesasCategorias',
    component: DespesasCategoriasPage,
    meta: {
      title: 'Categorias de Despesas',
      icon: 'pi-tag',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigDespesas',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/despesas_categorias'
    }
  },

  // Config > Despesas > Status
  {
    path: '/despesas-status',
    name: 'DespesasStatus',
    component: DespesasStatusPage,
    meta: {
      title: 'Status de Despesas',
      icon: 'pi-info-circle',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigDespesas',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/despesas_status'
    }
  },

  // Chamados — o canal de suporte franquia -> franqueadora (docs/plans/chamados-plan.md).
  //
  // `permissions` inclui TODOS os grupos operacionais de proposito: qualquer
  // funcionario precisa conseguir PEDIR ajuda. Quem RESPONDE e so `Full`, e
  // essa metade e decidida no backend a cada request — nao aqui. Fechar a rota
  // para Operador/Atendente deixaria justamente quem opera o dia a dia sem
  // canal.
  {
    path: '/chamados',
    name: 'Chamados',
    component: ChamadosPage,
    meta: {
      title: 'Chamados',
      icon: 'pi-comments',
      showInMenu: true,
      menuOrder: 90,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'chamados'
    }
  },

  // Config > Atendimento (Parent)
  {
    path: '/config-atendimento',
    name: 'ConfigAtendimento',
    redirect: '/servicos',
    meta: {
      title: 'Atendimento',
      icon: 'pi-briefcase',
      showInMenu: true,
      menuOrder: 30,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // Config > Atendimento > Serviços
  {
    path: '/servicos',
    name: 'Servicos',
    component: ServicosPage,
    meta: {
      title: 'Serviços',
      icon: 'pi-briefcase',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigAtendimento',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/servicos'
    }
  },

  // Config > Atendimento > Categorias de Serviços
  {
    path: '/servicos-categorias',
    name: 'ServicosCategorias',
    component: ServicosCategoriasPage,
    meta: {
      title: 'Categorias de Serviços',
      icon: 'pi-tags',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigAtendimento',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/servicos_categorias'
    }
  },

  // Config > Atendimento > Origens
  {
    path: '/origens',
    name: 'Origens',
    component: OrigensPage,
    meta: {
      title: 'Origens',
      icon: 'pi-map-marker',
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'ConfigAtendimento',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/origens'
    }
  },

  // Config > Administração (Parent)
  {
    path: '/config-admin',
    name: 'ConfigAdmin',
    redirect: '/franquias',
    meta: {
      title: 'Administração',
      icon: 'pi-cog',
      showInMenu: true,
      menuOrder: 40,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // Config > Administração > Franquias
  {
    path: '/franquias',
    name: 'Franquias',
    component: FranquiasPage,
    meta: {
      title: 'Franquias',
      icon: 'pi-building',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigAdmin',
      permissions: ['Full'],
      controller: 'config/franquias'
    }
  },
  // Config > Administração > Franquias > Configurações Avançadas
  {
    path: '/franquias/configuracoes-avancadas/:id',
    name: 'FranquiasConfiguracoesAvancadas',
    component: FranquiasConfigAvancadaPage,
    meta: {
      title: "Configurações Avançadas",
      icon: "pi-cog",
      showInMenu: false,
      permissions: ["Full", "Franqueado", "Gerente"]
    }
  },

  // Config > Administração > Usuários
  {
    path: '/usuarios',
    name: 'Users',
    component: UsersPage,
    meta: {
      title: 'Usuários',
      icon: 'pi-user',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigAdmin',
      // Quem gerencia usuários vem do app-api (degrau gestor ou administração delegada de um
      // app, skill app-groups) — nunca uma lista de grupos aqui.
      capability: 'gerencia_usuarios',
      controller: 'usuarios'
    }
  },

  // Config > Administração > Elementos PDF
  {
    path: '/pdf-elements',
    name: 'PDFElements',
    component: PDFElementsPage,
    meta: {
      title: 'Elementos PDF',
      icon: 'pi-file-pdf',
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'ConfigAdmin',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'config/pdf_elements'
    }
  },

  // Config > Administração > LGPD (registros de aceite de termos)
  {
    path: '/lgpd-registros',
    name: 'LgpdRegistros',
    component: LgpdRegistrosPage,
    meta: {
      title: 'LGPD',
      icon: 'pi-shield',
      showInMenu: true,
      menuOrder: 90,
      menuParent: 'ConfigAdmin',
      permissions: ['Full'],
      controller: 'lgpd_terms'
    }
  },

  // Config > Técnicas (Parent)
  //
  // Chamado 2026-0019: este ramo inteiro — o pai e as sete telas — aceita o
  // grupo `Tecnicas` além do `Full`. É a razão de o grupo existir: quem cuida da
  // máquina (logs, schema, jobs, webhooks) precisava de `Full`, que abre a rede
  // inteira, para ler um log. O `Tecnicas` abre EXATAMENTE estas sete e nada
  // mais — nem assinaturas, nem gateways, nem usuários, nem franquias. Os
  // controllers de cada uma repetem a lista: a tela escondida é conveniência, o
  // gate é no backend.
  //
  // Chamado 2026-0018: as telas que configuram a MÁQUINA (schema, jobs,
  // webhooks, logs) estavam soltas entre as que configuram o NEGÓCIO
  // (categorias, formas de pagamento, usuários). Este ramo passou a ser o
  // lugar delas. O `name` continua 'ConfigSistema' de propósito: é o que os
  // quatro filhos já existentes referenciam em menuParent, e renomeá-lo
  // trocaria sete linhas para não mudar nada na tela.
  {
    path: '/config-sistema',
    name: 'ConfigSistema',
    redirect: '/logs',
    meta: {
      title: 'Técnicas',
      icon: 'pi-wrench',
      showInMenu: true,
      menuOrder: 50,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Tecnicas'],
      isParent: true
    }
  },

  // Config > Sistema > Logs
  {
    path: '/logs',
    name: 'Logs',
    component: LogsPage,
    meta: {
      title: 'Logs',
      icon: 'pi-book',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      controller: 'config/logs'
    }
  },

  // Config > Sistema > Tutoriais Report
  {
    path: '/tutorials-report',
    name: 'TutorialsReport',
    component: () => import('@/domains/tutorials/TutorialsReportPage.vue'),
    meta: {
      title: 'Relatório de Tutoriais',
      icon: 'pi-question-circle',
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      controller: 'tutorial',
      requiresAuth: true
    }
  },

  // Config > Sistema > Webhook Logs
  {
    path: '/webhook-logs',
    name: 'WebhookLogs',
    component: () => import('@/domains/webhook-logs/WebhookLogsPage.vue'),
    meta: {
      title: 'Logs de Webhook',
      icon: 'pi-server',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      controller: 'config/webhook_logs',
      requiresAuth: true
    }
  },

  // Config > Sistema > Migração Zoho
  //
  // `name: 'Migration'` importa: o filtro do menu chama
  // `config.isFeatureEnabled(route.name)` com o nome em minúsculas, então a
  // chave do gate é `migration` — a mesma que o resto da feature usa. Um deploy
  // que não migra nada põe `migration` em AWRENV_SISTEMAAPI_DISABLED_FEATURES e o item some
  // do menu; a própria página ainda checa o gate, porque a URL direta continua
  // navegável e o pré-voo faz chamada ao Zoho.
  //
  // Permissão Full e só ela, espelhando o controller: migrar cria franquia,
  // subconta e credencial fiscal — é operação de plataforma.
  {
    path: '/migration',
    name: 'Migration',
    component: () => import('@/domains/migration/MigrationPage.vue'),
    meta: {
      title: 'Migração Zoho',
      icon: 'pi-cloud-download',
      showInMenu: true,
      menuOrder: 4,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      controller: 'migration/franquias',
      requiresAuth: true
    }
  },



  {
    path: '/whatsapp-message-templates',
    name: 'WhatsappMessageTemplates',
    component: WhatsappMessageTemplatesPage,
    meta: {
      title: 'Mensagens Prontas WhatsApp',
      icon: 'pi-comment',
      showInMenu: true,
      menuOrder: 301,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado'],
      controller: 'config/whatsapp_message_templates'
    }
  },

  {
    path: '/devices-franquias',
    name: 'DevicesFranquias',
    component: DevicesFranquiasPage,
    meta: {
      title: 'Dispositivos',
      icon: 'pi-mobile',
      showInMenu: true,
      menuOrder: 300,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado'],
      controller: 'config/devices_franquias'
    }
  },

  // Instagram Accounts (Config)
  {
    path: '/instagram-accounts',
    name: 'InstagramAccounts',
    component: InstagramAccountsPage,
    meta: {
      title: 'Instagram Accounts',
      icon: 'pi-instagram',
      showInMenu: true,
      menuOrder: 302,
      menuParent: 'Mensagens',
      permissions: ['Full', 'Franqueado'],
      controller: 'instagram/accounts'
    }
  },
  {
    path: '/instagram-oauth-callback',
    name: 'InstagramOAuthCallback',
    component: InstagramOAuthCallback,
    meta: { requiresAuth: true, showInMenu: false }
  },

  // Config - Ordens de Serviço (sub-group inside Configuracoes)
  {
    path: '/config-os',
    name: 'ConfigOS',
    redirect: '/so-service-categories',
    meta: {
      title: 'Ordens de Serviço',
      icon: 'pi-clipboard',
      showInMenu: true,
      menuOrder: 35,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      isParent: true
    }
  },

  // Config - OS - Categorias de Serviço
  {
    path: '/so-service-categories',
    name: 'SOServiceCategories',
    component: SOServiceCategoriesPage,
    meta: {
      title: 'Categorias de Serviço',
      icon: 'pi-tags',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/service_categories'
    }
  },

  // Config - OS - Categorias de Produto
  {
    path: '/so-product-categories',
    name: 'SOProductCategories',
    component: SOProductCategoriesPage,
    meta: {
      title: 'Categorias de Produto',
      icon: 'pi-tags',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/product_categories'
    }
  },

  // Config - OS - Prioridades
  {
    path: '/so-priorities',
    name: 'SOPriorities',
    component: SOPrioritiesPage,
    meta: {
      title: 'Prioridades',
      icon: 'pi-sort-amount-up',
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/priorities'
    }
  },

  // Config - OS - Atributos de Item
  {
    path: '/so-item-attributes',
    name: 'SOItemAttributes',
    component: SOItemAttributesPage,
    meta: {
      title: 'Atributos de Item',
      icon: 'pi-tags',
      showInMenu: true,
      menuOrder: 9,
      menuGroup: 'Config - OS',
      requiredGroups: ['Full', 'Franqueado', 'Gerente'],
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/item_attributes'
    }
  },

  // Config - OS - Atributos de Laudo
  {
    path: '/so-report-attributes',
    name: 'SOReportAttributes',
    component: SOReportAttributesPage,
    meta: {
      title: 'Atributos de Laudo',
      icon: 'pi-list',
      showInMenu: true,
      menuOrder: 10,
      menuGroup: 'Config - OS',
      requiredGroups: ['Full', 'Franqueado', 'Gerente'],
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/report_attributes'
    }
  },

  // Config - OS - Dados Fiscais do Catálogo
  {
    path: '/so-catalog-fiscal',
    name: 'SOCatalogFiscal',
    component: SOCatalogFiscalPage,
    meta: {
      title: 'Dados Fiscais do Catálogo',
      icon: 'pi-receipt',
      showInMenu: true,
      menuOrder: 11,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/service_fiscal'
    }
  },

  // Config - OS - Serviços
  {
    path: '/so-services',
    name: 'SOServices',
    component: SOServicesPage,
    meta: {
      title: 'Serviços',
      icon: 'pi-wrench',
      showInMenu: true,
      menuOrder: 4,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/services'
    }
  },

  // Config - OS - Servicos Adicionais
  {
    path: '/so-additional-services',
    name: 'SOAdditionalServices',
    component: SOAdditionalServicesPage,
    meta: {
      title: 'Serviços Adicionais',
      icon: 'pi-plus-circle',
      showInMenu: true,
      menuOrder: 7,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/additional_services'
    }
  },

  // Config - OS - Etapas de Producao
  {
    path: '/so-production-steps',
    name: 'SOProductionSteps',
    component: SOProductionStepsPage,
    meta: {
      title: 'Etapas de Produção',
      icon: 'pi-list-check',
      showInMenu: true,
      menuOrder: 8,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/production_steps'
    }
  },

  // Config - OS - Matriz Serviço-Prioridade
  {
    path: '/so-service-priorities',
    name: 'SOServicePriorities',
    component: SOServicePrioritiesPage,
    meta: {
      title: 'Matriz Serviço-Prioridade',
      icon: 'pi-th-large',
      showInMenu: true,
      menuOrder: 6,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/service_priorities'
    }
  },

  // Config - OS - Preços de Adicionais por Franquia (a irmã da matriz, para
  // adicionais: identidade global + preço/prazo por franquia — camada E3)
  {
    path: '/so-additional-service-prices',
    name: 'SOAdditionalServicePrices',
    component: SOAdditionalServicePricesPage,
    meta: {
      title: 'Preços de Adicionais por Franquia',
      icon: 'pi-dollar',
      showInMenu: true,
      menuOrder: 7,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/additional_service_prices'
    }
  },

  // Config - OS - Combos pré-pagos (NS 2026-0090): catálogo global, só o Full.
  // meta.feature porque as duas rotas são da MESMA feature `combos`.
  {
    path: '/so-combos',
    name: 'SOCombos',
    component: SOCombosPage,
    meta: {
      title: 'Combos',
      icon: 'pi-box',
      showInMenu: true,
      menuOrder: 8,
      menuParent: 'ConfigOS',
      permissions: ['Full'],
      feature: 'combos',
      controller: 'so/combos'
    }
  },

  // Config - OS - Preço dos combos por unidade. Gerente lê; Full e Franqueado gravam.
  {
    path: '/so-combo-prices',
    name: 'SOComboPrices',
    component: SOComboPricesPage,
    meta: {
      title: 'Preço dos combos',
      icon: 'pi-dollar',
      showInMenu: true,
      menuOrder: 9,
      menuParent: 'ConfigOS',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      feature: 'combos',
      controller: 'so/combo_prices'
    }
  },

  // Ordens de Serviço - Laudo de Entrada (hidden)
  {
    path: '/so-entry-report/:orderItemId',
    name: 'SOEntryReport',
    component: SOEntryReportPage,
    meta: {
      title: 'Laudo de Entrada',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'so/entry_reports',
      requiresAuth: true
    }
  },

  // PDV - Ponto de Venda (Parent)
  {
    path: '/pdv',
    name: 'PDV',
    redirect: '/pdv/lista',
    meta: {
      title: 'PDV',
      icon: 'pi-shopping-cart',
      showInMenu: true,
      menuOrder: 46,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      isParent: true
    }
  },

  // PDV - Lista de Ordens
  {
    path: '/pdv/lista',
    name: 'PDVLista',
    component: PDVPage,
    meta: {
      title: 'Frente de Caixa',
      icon: 'pi-shopping-cart',
      showInMenu: true,
      menuOrder: 1,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders'
    }
  },

  // PDV - Caixa (legacy bookmark redirect → /pdv/lista)
  {
    path: '/pdv/caixa',
    redirect: '/pdv/lista'
  },

  // PDV - Novo Atendimento
  {
    path: '/pdv/novo-atendimento',
    name: 'PDVNovoAtendimento',
    component: PDVNovoAtendimentoPage,
    meta: {
      title: 'Novo Atendimento',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
    }
  },

  // PDV - Checkout (page, not modal)
  {
    path: '/pdv/checkout/:orderId',
    name: 'PDVCheckoutPage',
    component: PDVCheckoutPage,
    meta: {
      requiresAuth: true,
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      title: 'Checkout',
    }
  },

  // PDV - Laudos de Entrada
  {
    path: '/pdv/laudos-entrada',
    name: 'PDVLaudosEntrada',
    component: PDVLaudosEntradaPage,
    meta: {
      title: 'Laudos de Entrada',
      icon: 'pi-camera',
      showInMenu: true,
      menuOrder: 2,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Laudos de Saída
  {
    path: '/pdv/laudos-saida',
    name: 'PDVLaudosSaida',
    component: PDVLaudosSaidaPage,
    meta: {
      title: 'Laudos de Saída',
      icon: 'pi-camera',
      showInMenu: true,
      menuOrder: 5,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Scanner de Produção
  {
    path: '/pdv/scanner',
    name: 'PDVScanner',
    component: PDVScannerPageV2,
    meta: {
      requiresAuth: true,
      showInMenu: true,
      menuOrder: 3,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      title: 'Scanner Produção',
      icon: 'pi-qrcode',
      controller: 'so/orders',
      pageTitle: 'Scanner Produção',
    }
  },

  // PDV - Painel TV
  {
    path: '/pdv/tv',
    name: 'PDVTV',
    component: PDVTVPage,
    meta: {
      requiresAuth: true,
      showInMenu: true,
      menuOrder: 7,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      title: 'Painel TV',
      icon: 'pi-desktop',
    }
  },

  // PDV - Agenda de produção (Full-only)
  {
    path: '/pdv/agenda-mensal',
    name: 'PDVAgendaMensal',
    component: PDVAgendaMensalPage,
    meta: {
      requiresAuth: true,
      showInMenu: true,
      menuOrder: 8,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      title: 'Agenda de produção',
      icon: 'pi-calendar',
    }
  },

  // PDV - Producao
  {
    path: '/pdv/producao',
    name: 'PDVProducao',
    component: PDVProducaoPage,
    meta: {
      title: 'Produção',
      icon: 'pi-cog',
      showInMenu: true,
      menuOrder: 4,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Movimentacao (hidden, accessed via QR code search)
  {
    path: '/pdv/movimentacao',
    name: 'PDVMovimentacao',
    component: PDVMovimentacaoPage,
    meta: {
      title: 'Movimentação',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Exit Report (hidden, accessed via route param)
  {
    path: '/pdv/exit-report/:orderItemId',
    name: 'PDVExitReport',
    component: PDVExitReportPage,
    meta: {
      title: 'Laudo de Saída',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Entregas
  {
    path: '/pdv/entregas',
    name: 'PDVEntregas',
    component: PDVEntregasPage,
    meta: {
      title: 'Entregas',
      icon: 'pi-truck',
      showInMenu: true,
      menuOrder: 6,
      menuParent: 'PDV',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders',
      requiresAuth: true
    }
  },

  // PDV - Estoque
  {
    path: '/pdv/estoque',
    name: 'SOStock',
    component: SOStockPage,
    meta: {
      title: 'Estoque',
      icon: 'pi-box',
      showInMenu: true,
      menuOrder: 48,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'so/stock'
    }
  },

  // Caixa
  {
    path: '/caixa/lista',
    name: 'CaixaLista',
    component: () => import('../domains/caixa/CaixaPage.vue'),
    meta: {
      title: 'Caixa',
      icon: 'pi-wallet',
      showInMenu: true,
      menuOrder: 62,
      menuParent: 'ControleCaixa',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'caixa'
    }
  },

  // Cofre
  {
    path: '/cofre/lista',
    name: 'CofreLista',
    component: () => import('../domains/cofre/CofrePage.vue'),
    meta: {
      title: 'Cofre',
      icon: 'pi-building-columns',
      showInMenu: true,
      menuOrder: 63,
      menuParent: 'ControleCaixa',
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'cofre'
    }
  },

  // Fechamento Diário
  {
    path: '/caixa/fechamento-diario',
    name: 'FechamentoDiario',
    component: () => import('../domains/caixa/FechamentoDiarioPage.vue'),
    meta: {
      title: 'Fechamento de Caixa',
      icon: 'pi-check-square',
      showInMenu: true,
      menuOrder: 64,
      menuParent: 'ControleCaixa',
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'caixa'
    }
  },

  // PDV - Ordens de Serviço
  {
    path: '/so-orders',
    name: 'SOOrders',
    component: SOOrdersPage,
    meta: {
      title: 'Ordens de Serviço',
      icon: 'pi-list',
      showInMenu: true,
      menuOrder: 47,
      menuParent: null,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'so/orders'
    }
  },

  // // Config - Debug User Data
  // {
  //   path: '/debug/user-data',
  //   name: 'DebugUserData',
  //   component: DebugUserDataPage,
  //   meta: {
  //     title: 'Debug: User Data',
  //     icon: 'pi-wrench',
  //     showInMenu: true,
  //     menuOrder: 313,
  //     menuParent: 'Configuracoes',
  //     permissions: ['Full', 'Operador'],
  //     controller: null
  //   }
  // },

  // Hidden routes (not in menu)
  {
    path: '/compromisso-servicos',
    name: 'CompromissoServicos',
    component: CompromissoServicoPage,
    meta: {
      title: 'Serviços de Compromissos',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'compromisso_servicos'
    }
  },

  {
    path: '/orcamento-servicos',
    name: 'OrcamentoServicos',
    component: OrcamentoServicoPage,
    meta: {
      title: 'Serviços de Orçamentos',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'orcamento_servicos'
    }
  },

  {
    path: '/ocorrencias',
    name: 'Ocorrencias',
    component: OccurrencesPage,
    meta: {
      title: 'Ocorrências',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'ocorrencias'
    }
  },

  // Chamado 2026-0021: a tela de retomada mora ao lado do contato de fidelização —
  // as duas são trabalho de relacionamento da franquia com quem já é cliente. A
  // diferença é o gatilho: aquela olha quem sumiu há 30 dias, esta olha o prazo de
  // retorno DA CATEGORIA do serviço que a pessoa fez.
  {
    path: '/retomada',
    name: 'Retomada',
    component: () => import('@/domains/retomada/RetomadaPage.vue'),
    meta: {
      title: 'Retomada de clientes',
      icon: 'pi-replay',
      showInMenu: true,
      // Irmã de "Gestão de Clientes" (menuOrder 20) e não filha: aquela rota é
      // FOLHA, não pai — pendurar nela deixaria o item órfão na árvore do menu.
      menuParent: null,
      menuOrder: 21,
      requiresAuth: true,
      // Atendente desde o chamado 2026-0052: é quem faz os contatos da fila.
      permissions: ['Full', 'Franqueado', 'Gerente', 'Atendente'],
      controller: 'retomada'
    }
  },

  {
    path: '/contato-fidelizacao',
    name: 'ContatoFidelizacao',
    component: ContatoFidelizacaoPage,
    meta: {
      title: 'Contato de Fidelização',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente'],
      controller: 'contato_fidelizacao'
    }
  },

  // Support Tickets
  {
    path: '/suporte/chamados',
    name: 'SupportTickets',
    component: TicketsPage,
    meta: {
      title: 'Meus Chamados',
      icon: 'pi-ticket',
      showInMenu: false,
      permissions: ['Full', 'Franqueado', 'Gerente', 'Operador', 'Atendente'],
      controller: 'odoo_tickets',
      requiresAuth: true
    }
  },

  // Legal pages (public - no auth required)
  {
    path: '/politica-de-privacidade',
    name: 'PoliticaPrivacidade',
    component: () => import('@/domains/legal/PoliticaPrivacidadePage.vue'),
    meta: {
      title: 'Política de Privacidade',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/termos-de-servico',
    name: 'TermosServico',
    component: () => import('@/domains/legal/TermosServicoPage.vue'),
    meta: {
      title: 'Termos de Serviço',
      requiresAuth: false,
      showInMenu: false
    }
  },
  {
    path: '/preencher-cadastro/:token',
    name: 'CustomerPreCadastro',
    component: () => import('@/domains/public/CustomerPreCadastroPage.vue'),
    meta: {
      title: 'Complete seu Cadastro',
      requiresAuth: false,
      showInMenu: false,
    },
  },
  {
    path: '/laudo/:token',
    name: 'PublicLaudo',
    component: () => import('@/domains/public/PublicLaudoPage.vue'),
    meta: {
      title: 'Laudo',
      requiresAuth: false,
      showInMenu: false,
    },
  },
  {
    path: '/data-deletion',
    name: 'DataDeletion',
    component: () => import('@/domains/legal/DataDeletionPage.vue'),
    meta: {
      title: 'Data Deletion Instructions',
      requiresAuth: false,
      showInMenu: false
    }
  },

  // Config > Traduções (Parent)
  {
    path: '/config-traducoes',
    name: 'TraducoesSection',
    redirect: '/admin/translations',
    meta: {
      title: 'Traduções',
      icon: 'pi-language',
      showInMenu: true,
      menuOrder: 60,
      menuParent: 'Configuracoes',
      permissions: ['Full', 'Master'],
      isParent: true
    }
  },

  // Config > Traduções > Traduções
  {
    path: '/admin/translations',
    name: 'translations-admin',
    component: TranslationsPage,
    meta: {
      title: 'Traduções',
      icon: 'pi-language',
      showInMenu: true,
      menuOrder: 10,
      menuParent: 'TraducoesSection',
      permissions: ['Full', 'Master'],
      requiresAuth: true
    }
  },

  // Config > Traduções > Idiomas
  {
    path: '/admin/languages',
    name: 'languages-admin',
    component: LanguagesPage,
    meta: {
      title: 'Idiomas',
      icon: 'pi-globe',
      showInMenu: true,
      menuOrder: 20,
      menuParent: 'TraducoesSection',
      permissions: ['Full', 'Master'],
      requiresAuth: true
    }
  },

  // Billing 360 — per-franchise composite observability (Full-only, S5 FE).
  {
    path: '/config/billing-360',
    name: 'Billing360',
    component: () => import('@/domains/staff/observability/BillingThreeSixtyPage.vue'),
    meta: {
      title: 'Billing 360',
      icon: 'pi-chart-bar',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 5,
    },
  },

  // Staff Transactions — platform-wide revenue ledger (Full-only).
  {
    path: '/config/transacoes',
    name: 'StaffTransactions',
    component: () => import('@/domains/staff/transactions/StaffTransactionsPage.vue'),
    meta: {
      title: 'Transações',
      icon: 'pi-credit-card',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 6,
    },
  },

  // Schema Migrations — the schema runner's staff page (Full-only), plan
  // docs/plans/schema-migrations-runner-plan.md P3.
  {
    path: '/config/migracoes',
    name: 'SchemaMigrations',
    component: () => import('@/domains/staff/schema-migrations/SchemaMigrationsPage.vue'),
    meta: {
      title: 'Migrações de Schema',
      icon: 'pi-database',
      requiresAuth: true,
      requiresGroups: ['Full', 'Tecnicas'],
      showInMenu: true,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      menuOrder: 5,
    },
  },

  // Config > Site institucional (Parent) — the three panels of the
  // institutional website live together under one group, and the group itself
  // gates on the tenant feature `site`, so a tenant without the site sees no
  // empty folder in the sidebar. It has no page of its own: it redirects to
  // the units panel, the one a marketing person opens most.
  {
    path: '/config-site-institucional',
    name: 'SiteInstitucional',
    redirect: '/config/site-unidades',
    meta: {
      title: 'Site institucional',
      icon: 'pi-globe',
      showInMenu: true,
      menuOrder: 11,
      menuParent: 'Configuracoes',
      permissions: ['Full'],
      feature: 'site',
      isParent: true,
    },
  },

  // Unidades do site — staff panel for the institutional website's units
  // (site-service). Full-only. Route name MUST stay exactly 'Site' — the
  // router's feature gate does config.isFeatureEnabled(to.name) on the
  // lowercased route name, and the tenant feature key for this panel is
  // 'site' (see site-service-orchestrator-integration-plan.md).
  {
    path: '/config/site-unidades',
    name: 'Site',
    component: () => import('@/domains/site-unidades/SiteUnidadesPage.vue'),
    meta: {
      title: 'Unidades do site',
      icon: 'pi-map',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'SiteInstitucional',
      permissions: ['Full'],
      menuOrder: 10,
    },
  },

  // Leads do site — staff panel for the institutional website's leads
  // (site-service webhook → site_leads). Full-only, read-mostly: the one
  // write moves the pipeline status and attaches an internal note.
  //
  // The route name cannot be 'Site' (taken by the units panel above), so the
  // feature key is declared explicitly: both pages belong to the tenant
  // feature 'site' and must appear and disappear together.
  // Webhooks da plataforma para plugins (docs/plans/plataforma-e-tenants.md §14):
  // console Full-only — catálogo, assinaturas, test-send, log de entregas.
  {
    path: '/config/webhooks',
    name: 'PlatformWebhooks',
    component: () => import('@/domains/platform-webhooks/PlatformWebhooksPage.vue'),
    meta: {
      title: 'Webhooks da plataforma',
      icon: 'pi-send',
      requiresAuth: true,
      requiresGroups: ['Full', 'Tecnicas'],
      showInMenu: true,
      menuOrder: 7,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      feature: 'platformwebhooks',
    },
  },
  {
    path: '/config/site-leads',
    name: 'SiteLeads',
    component: () => import('@/domains/site-leads/SiteLeadsPage.vue'),
    meta: {
      title: 'Leads do site',
      icon: 'pi-inbox',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'SiteInstitucional',
      permissions: ['Full'],
      feature: 'site',
      menuOrder: 20,
    },
  },

  // Templates de e-mail do site — the text and the recipients of each form's
  // notification. Full-only, same tenant feature as the other two panels, so
  // the key is declared explicitly (the route name is not 'Site').
  {
    path: '/config/site-templates',
    name: 'SiteTemplates',
    component: () => import('@/domains/site-templates/SiteTemplatesPage.vue'),
    meta: {
      title: 'Templates de e-mail',
      icon: 'pi-envelope',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'SiteInstitucional',
      permissions: ['Full'],
      feature: 'site',
      menuOrder: 30,
    },
  },

  // Scheduled Jobs — S10 administrable recurring processes console (Full-only).
  {
    path: '/config/processos-recorrentes',
    name: 'ScheduledJobs',
    component: () => import('@/domains/staff/scheduling/ScheduledJobsPage.vue'),
    meta: {
      title: 'Processos Recorrentes',
      icon: 'pi-clock',
      requiresAuth: true,
      requiresGroups: ['Full', 'Tecnicas'],
      showInMenu: true,
      menuParent: 'ConfigSistema',
      permissions: ['Full', 'Tecnicas'],
      menuOrder: 6,
    },
  },

  // Rede de unidades — o painel geral da rede e a situação de cada unidade (chamado NS 2026-0082).
  {
    path: '/config/rede',
    name: 'RedeUnidades',
    component: () => import('@/domains/rede/RedePage.vue'),
    meta: {
      title: 'Rede de unidades',
      icon: 'pi-sitemap',
      requiresAuth: true,
      showInMenu: true,
      menuParent: 'Configuracoes',
      // Quem entra vem do app-api (Full ou o grupo do jurídico que o tenant declara, skill
      // app-groups) — nunca uma lista de grupos aqui.
      capability: 'rede_de_unidades',
      menuOrder: 1,
    },
  },

  // Chamados > Configuração — logo do e-mail de aviso (Full-only).
  {
    path: '/config/chamados',
    name: 'ChamadosConfig',
    component: () => import('@/domains/chamados/ChamadosConfigPage.vue'),
    meta: {
      title: 'Chamados',
      icon: 'pi-image',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'Configuracoes',
      permissions: ['Full'],
      menuOrder: 10,
    },
  },

  // Franchisor Channels — S13 franchisor→franqueado communication console (Full-only).
  {
    path: '/config/canais',
    name: 'FranchisorChannels',
    component: () => import('@/domains/staff/channels/FranchisorChannelsPage.vue'),
    meta: {
      title: 'Canais do franqueador',
      icon: 'pi-send',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      menuParent: 'Configuracoes',
      permissions: ['Full'],
      menuOrder: 9,
    },
  },

  // Subscription terms of agreement — Full-only staff console.
  // docs/plans/subscription-terms-of-agreement.md
  {
    path: '/config/termos-de-assinatura',
    name: 'SubscriptionTerms',
    component: () => import('@/domains/staff/subscription-terms/SubscriptionTermsPage.vue'),
    meta: {
      title: 'Termos de assinatura',
      icon: 'pi-file-edit',
      requiresAuth: true,
      requiresGroups: ['Full'],
      showInMenu: true,
      // Sits with the billing group: the agreement is per PLAN, so it belongs
      // beside the plans it hangs off, not in the generic Configurações list.
      menuParent: 'ConfigSetupAssinaturaIa',
      permissions: ['Full'],
      menuOrder: 9,
    },
  },

  // Catch-all 404 route (must be last)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

// Rotas dos módulos de plugin (src/modules/*/manifest.ts) entram ANTES do
// catch-all, quando ele existe, para que um path de plugin não caia no 404.
const catchAllAt = routes.findIndex((r) => String(r.path).startsWith('/:pathMatch'))
const allRoutes: RouteRecordRaw[] = catchAllAt === -1
  ? [...routes, ...pluginRoutes]
  : [...routes.slice(0, catchAllAt), ...pluginRoutes, ...routes.slice(catchAllAt)]

const router = createRouter({
  history: createWebHistory(config.baseUrl),
  routes: allRoutes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  log('Router', 'beforeEach', `Navigating from ${from.path} to ${to.path}`, {
    toName: to.name,
    toMeta: to.meta
  })

  // IMPORTANT: Log when fechamentos route is accessed
  if (to.path === '/fechamentos' || to.name === 'Fechamentos') {
    log('Router', 'beforeEach', 'FECHAMENTOS ROUTE ACCESSED', {
      name: to.name,
      path: to.path,
      matched: to.matched.map(r => ({ name: r.name, path: r.path }))
    });
  }

  // Update document title
  if (to.meta.title) {
    document.title = `${to.meta.title} - Franchise Factory`
  }

  // Public routes that don't require authentication
  if (to.meta.requiresAuth === false) {
    log('Router', 'beforeEach', 'Public route - no auth check required')
    next()
    return
  }

  // Check internal token validation
  log('Router', 'beforeEach', 'Checking internal token status')

  const tokenStatus = tokenService.getTokenStatus()
  log('Router', 'beforeEach', `Token status: ${tokenStatus}`)

  if (tokenStatus === 'EXPIRED' || tokenStatus === 'INVALID') {
    log('Router', 'beforeEach', 'Token invalid/expired - redirecting to login')

    // Redirect to login with return URL
    const redirectTo = to.fullPath
    next({
      path: '/login',
      query: { redirectTo }
    })
    return
  }

  if (tokenStatus === 'EXPIRING_SOON') {
    log('Router', 'beforeEach', '⚠️ Token expiring soon - proactive refresh will trigger automatically')
    // Proactive refresh is handled by tokenService scheduler
  }

  // Feature registry (backend config/features.php via tenant_config →
  // config.setDisabledFeatures): a disabled feature is not only hidden from
  // the sidebar (routeMetadata.getRoutesForMenu) — its URL is closed too.
  // Same key convention as the menu: the route name, lowercased.
  const featureKey = featureKeyOf(to)
  if (featureKey !== null && !config.isFeatureEnabled(featureKey)) {
    log('Router', 'beforeEach', `Feature disabled for this tenant - redirecting: ${String(to.name)} (feature ${featureKey})`)
    next({ path: '/' })
    return
  }

  // Fase A — franquia sem assinatura (decisão do usuário, 2026-08-12): lock
  // duro. allFranchisesInactive é SEMPRE false para Full (ver
  // useSubscriptionStatus), então o Full nunca cai neste bloco. Roda depois
  // do check de token (usuário autenticado) e antes do requiresGroups, para
  // nunca deixar passar para uma tela de dados.
  const { allFranchisesInactive } = useSubscriptionStatus()
  if (allFranchisesInactive.value && !LOCK_EXEMPT_ROUTE_NAMES.has(String(to.name))) {
    const destination = resolveLockDestination(permissionsState.user?.groups)
    log('Router', 'beforeEach', 'Lock: franquia sem assinatura ativa — redirecionando', { destination })
    next({ path: destination })
    return
  }

  // Capacidade exigida (skill app-groups): a mesma regra do menu (hasAccess).
  if (to.meta.capability) {
    if (!permissionsState.permissionsLoaded) {
      try {
        await permissionsService.initialize()
      } catch (error) {
        log('Router', 'beforeEach', 'Permissions load failed during capability check', { error })
      }
    }
    if (!hasCapability(to.meta.capability, permissionsState.capacidades)) {
      log('Router', 'beforeEach', 'Missing capability - redirecting to home', { capability: to.meta.capability })
      next({ path: '/' })
      return
    }
  }

  // Check group permissions if requiresGroups is set
  if (to.meta.requiresGroups && to.meta.requiresGroups.length > 0) {
    log('Router', 'beforeEach', 'Checking group permissions', { requiresGroups: to.meta.requiresGroups })

    // On a cold deep-link the guard can run before the app bootstrap has
    // populated permissionsState.user — checking groups now would spuriously
    // redirect a permitted user. Ensure permissions are loaded first.
    // initialize() is idempotent (it awaits an in-flight init and is
    // cache-memoized); a failure here is non-fatal — the token check above
    // already gates auth, and the JWT + app-api scoping is the real boundary.
    if (!permissionsState.permissionsLoaded) {
      try {
        await permissionsService.initialize()
      } catch (error) {
        log('Router', 'beforeEach', 'Permissions load failed during group check - proceeding (server-side scoping is authoritative)', { error })
      }
    }

    const userGroups = permissionsState.user?.groups || []
    const hasRequiredGroup = to.meta.requiresGroups.some(group => userGroups.includes(group))

    if (!hasRequiredGroup) {
      log('Router', 'beforeEach', 'User does not have required groups - redirecting to home', {
        userGroups,
        requiresGroups: to.meta.requiresGroups
      })
      next({ path: '/' })
      return
    }
  }

  // Token is valid - proceed to route
  log('Router', 'beforeEach', 'Token valid - proceeding to route')
  next()
})

router.afterEach((to, from) => {
  log('Router', 'afterEach', `Navigation completed to ${to.path} from ${from.path}`)
})

export default router
// Force reload
